import { useCategoriesOptions } from "api/categories/useCategoriesOptions";
import {
  NOTIFICATION_TYPE_OPTIONS,
  NotificationApi,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotifications,
} from "api/notifications";
import { PageHeader, PageMain } from "components/molecules";
import { IDropdownOption, MultiSelect } from "components/atoms";
import { TanTable, TanTableColumn, useFilters, useInitFilters, usePagination } from "components/molecules";
import { buildApiFilter } from "components/molecules/TanTable/TanTable.consts";
import { formatDate, getRelativeTime } from "utils";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { htmlParser } from "utils/htmlParser";
import { resolveLink } from "./NotificationItem";

const FILTER_NAME = "notifications-filter";

export const NotificationsPage = (): JSX.Element => {
  const { getInitFilterValues } = useInitFilters(FILTER_NAME);

  return (
    <NotificationsPageInner
      initValues={{
        category: getInitFilterValues("category"),
        type: getInitFilterValues("type"),
      }}
    />
  );
};

interface NotificationsPageInnerProps {
  initValues: {
    category: number[];
    type: number[];
  };
}

export const NotificationsPageInner = ({ initValues }: NotificationsPageInnerProps): JSX.Element => {
  const { filter, changeFilter, updateAllFilters } = useFilters(FILTER_NAME);
  const { loading: categoriesLoading, options: categoryOptions } = useCategoriesOptions();
  const [selectedCategories, setSelectedCategories] = useState<number[]>(initValues.category);
  const { limit, skip, onPaginationChange, pagination } = usePagination();
  const { isLoading, isFetching, notifications, total, refetch } = useNotifications(skip, limit, filter);
  const navigate = useNavigate();

  const markAllAsReadMutation = useMarkAllNotificationsAsRead();
  const markAsReadMutation = useMarkNotificationAsRead();

  useEffect(() => {
    refetch();
  }, [pagination, selectedCategories, filter]);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsReadMutation.mutateAsync(selectedCategories.length > 0 ? selectedCategories : undefined);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }, [markAllAsReadMutation, selectedCategories]);

  const handleMarkAsRead = useCallback(
    async (notificationId: number) => {
      try {
        await markAsReadMutation.mutateAsync(notificationId);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    },
    [markAsReadMutation],
  );

  const handleRowClick = useCallback(
    async (notification: NotificationApi) => {
      if (!notification.isRead) {
        try {
          await markAsReadMutation.mutateAsync(notification.id);
        } catch (error) {
          console.error("Failed to mark notification as read:", error);
        }
      }

      const link = resolveLink(notification);
      if (link !== "#0") {
        navigate(link);
      }
    },
    [markAsReadMutation, navigate],
  );

  const columns = React.useMemo<TanTableColumn<NotificationApi>[]>(
    () => [
      {
        id: "notificationDate",
        header: "Data",
        accessorKey: "notificationDate",
        enableColumnFilter: false,
        enableSorting: false,
        size: 50,
        cell: (cell) => {
          const date = cell.getValue<Date>();
          return (
            <div className="min-w-[70px]">
              <div>{formatDate(date, true)}</div>
              <div className="text-xs text-gray-400">{getRelativeTime(date)}</div>
            </div>
          );
        },
      },
      {
        id: "courseGroup",
        hideOnMobile: true,
        header: "Grupa kursów",
        accessorKey: "relatedCourseGroupName",
        enableSorting: false,
        enableColumnFilter: false,
        size: 150,
      },
      {
        id: "type",
        hideOnMobile: true,
        header: "Typ",
        accessorKey: "type",
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          filterVariant: "select",
          options: NOTIFICATION_TYPE_OPTIONS,
        },
        cell: (cell) => {
          const type = cell.getValue<NotificationApi["type"]>();
          const typeOption = NOTIFICATION_TYPE_OPTIONS.find((opt) => opt.value === type);
          return <span>{typeOption ? typeOption.label : "Nieznany"}</span>;
        },
      },
      {
        id: "message",
        tdClassName: "max-md:[overflow-wrap:anywhere]",
        header: "Wiadomość",
        accessorKey: "message",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell) => htmlParser.toHTML(cell.getValue<string>()),
      },
      {
        id: "actions",
        header: "Akcje",
        enableSorting: false,
        enableColumnFilter: false,
        size: 40,
        cell: (cell) => {
          const notification = cell.row.original;
          return (
            <div className="flex gap-2">
              {!notification.isRead && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkAsRead(notification.id);
                  }}
                  className="text-sm text-blue-500 hover:text-blue-700"
                  title="Oznacz jako przeczytane"
                >
                  Oznacz
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [handleMarkAsRead],
  );

  return (
    <PageMain>
      <PageHeader>Powiadomienia</PageHeader>

      {/* Filters */}
      <div className="mb-6 rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="w-full sm:w-auto">
            <MultiSelect
              name="category"
              label="Kategoria"
              className="w-full md:min-w-[400px]"
              options={categoryOptions}
              value={categoryOptions.filter((opt) => selectedCategories.includes(opt.value as number))}
              onChange={(opts: IDropdownOption[]) => {
                changeFilter(
                  "category",
                  opts.length === 0 ? "" : `category in (${opts.map((opt) => opt.value).join(",")})`,
                );
                setSelectedCategories(opts.map((opt) => opt.value) as number[]);
              }}
              isLoading={categoriesLoading}
            />
          </div>
          <button
            onClick={handleMarkAllAsRead}
            disabled={markAllAsReadMutation.isPending}
            className="rounded bg-blue-500 px-3 py-1 text-sm text-white hover:bg-blue-600 disabled:opacity-50"
          >
            {markAllAsReadMutation.isPending ? "Oznaczanie..." : "Oznacz wszystkie jako przeczytane"}
          </button>
        </div>
      </div>

      <TanTable
        data={notifications}
        columns={columns}
        loading={isLoading}
        fetching={isFetching}
        total={total}
        onPaginationChange={onPaginationChange}
        pagination={pagination}
        onRowClick={handleRowClick}
        initialFilters={[
          {
            id: "type",
            value: initValues.type,
          },
        ]}
        onFiltersChange={(filters) => {
          const categoryFilter =
            selectedCategories.length === 0
              ? { id: "category", value: "", apiFilter: "" }
              : buildApiFilter({ id: "category", value: selectedCategories }, "select");
          updateAllFilters([...filters, categoryFilter].map((x) => ({ key: x.id, value: x.apiFilter })));
        }}
        onClearFilters={() => {
          setSelectedCategories([]);
          updateAllFilters([]);
        }}
      />
    </PageMain>
  );
};
